import ChatBar from "@/components/communications-chat-components/ChatBar";
import CommunicationsChatTextFieldComponent from "@/components/communications-chat-components/CommunicationsChatTextFieldComponent";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import { Box, Typography, styled } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem } from "react-mentions";
import { SendMessageFn } from "./ChatContainer";
import ChatMessageList from "./ChatMessageList";
import { useOneChatContext } from "./one/OneChatContext";

interface ChatViewProps {
  onChatSelect: (chat: LazoChatConversationShell) => void;
  userTyping: LazoChatTypingDTO;
  sendMessage: SendMessageFn;
  currentUserExternalId: string;
  setNewConversationMembers: Dispatch<SetStateAction<string[]>>;
}

export function ChatView({ onChatSelect, userTyping, sendMessage, currentUserExternalId, setNewConversationMembers }: ChatViewProps) {
  const { t } = useTranslation("chat");
  const [openSettings, setOpenSettings] = useState<boolean>(false);

  const {
    messages,
    setMessages,
    members,
    setMembers,
    messagesLoadingPage,
    messagesFirstLoad,
    fetchMessagesPage,
    chats,
    setChats,
    selectedChat,
    chatPreview,
    setChatPreview,
    readOnly,
  } = useOneChatContext();

  // Provider-level read-only (embedded history) or the per-chat read-only flag both disable sending.
  const isReadOnly = readOnly || !!selectedChat?.readOnly;

  function handleSendMessage(
    message: string,
    conversation: LazoChatConversationShell,
    mentions?: MentionItem[] | undefined,
    files?: File[] | undefined
  ) {
    // Creating conversation and conversation already exists, use preview
    if (chatPreview !== null) {
      conversation = mapToShell(chatPreview);
    }
    sendMessage(message, conversation, mentions, files);

    setChatPreview(null);
  }

  return (
    <StyledChatBox>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          transition: "opacity 0.2s ease-out",
          flex: { xs: 1, md: 2 },
          width: "100%",
          height: "100%",
          overflow: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
          alignItems: "stretch",
          borderTopRightRadius: { xs: 0, md: "0.75rem" },
        }}
      >
        <ChatBar
          setNewConversationMembers={setNewConversationMembers}
          setOpenSettings={setOpenSettings}
          conversation={selectedChat}
          onChatSelect={onChatSelect}
          chats={chats}
          setChats={setChats}
          members={members}
          chatPreview={chatPreview}
          setChatPreview={setChatPreview}
        />
        {messagesFirstLoad && <Spinner size={"1.5rem"} sx={{ color: Colors.LAZO_PURPLE }} />}
        {!messagesFirstLoad && (
          <ChatMessageList
            fetchMessagesPage={fetchMessagesPage}
            messagesLoadingPage={messagesLoadingPage}
            members={members}
            setMembers={setMembers}
            messages={chatPreview?.messages || messages}
            setMessages={setMessages}
            conversation={selectedChat}
            openSettings={openSettings}
            setOpenSettings={setOpenSettings}
            onChatSelect={onChatSelect}
            setChats={setChats}
          />
        )}
        {userTyping &&
          userTyping.conversation?.externalId == selectedChat?.externalId &&
          currentUserExternalId != userTyping.user.externalId && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "rgba(0, 0, 0, 0.54)",
                padding: "0.5rem",
              }}
            >
              <span
                style={{
                  fontSize: "0.9rem",
                  color: "BLACK",
                }}
              >
                {userTyping.user.firstName}{" "}
              </span>
              {t("chatView.userTyping")}
            </Typography>
          )}
      </Box>
      <ChatTextFieldContainer>
        {isReadOnly ? (
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: 800,
              textDecoration: "underline",
            }}
          >
            {t("chatView.readOnlyChat")}
          </Typography>
        ) : (
          <CommunicationsChatTextFieldComponent selectedChat={selectedChat} members={members} onSendMessage={handleSendMessage} />
        )}
      </ChatTextFieldContainer>
    </StyledChatBox>
  );
}

const StyledChatBox = styled(Box)({
  width: "100%",
  maxWidth: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  overflow: "auto",

  position: "relative",
  "& .chat-box-header": {
    position: "relative",
    zIndex: 1,
  },
  "& .chat-box-body": {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    overflow: "auto",
    padding: "1rem",
    height: "100%",
  },
  "& .chat-box-footer": {
    position: "relative",
    zIndex: 1,
  },
});

const ChatTextFieldContainer = styled(Box)({
  padding: "0 1em 1em 1em",
  backgroundColor: Colors.ACCENT_F1F3FF,
});

export default ChatView;
